import { useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { cn } from '@/utils/cn';

export interface TooltipProps {
  content: ReactNode;
  children: ReactNode;
  side?: 'top' | 'bottom';
  className?: string;
}

/** Space to keep clear of the viewport edge; matches the mb-1.5/mt-1.5 gap. */
const EDGE_MARGIN = 6;

type Align = 'center' | 'left' | 'right';

/**
 * Hover/focus tooltip. The trigger is described by the tooltip, so the content
 * is available to assistive technology rather than being purely visual.
 *
 * `side` is a preference, not a guarantee. On show this measures the actual
 * rendered box against the viewport and, before the browser paints
 * (useLayoutEffect, so there is no visible jump):
 * - flips top/bottom if the preferred side has no room (e.g. a header
 *   control sitting at the very top of the page);
 * - stops centering on the trigger and anchors to whichever side keeps the
 *   box fully on-screen, if a trigger near the left/right edge (e.g. the
 *   rightmost control in a header) would otherwise push a wide tooltip past
 *   that edge.
 */
export function Tooltip({ content, children, side = 'top', className }: TooltipProps) {
  const [visible, setVisible] = useState(false);
  const [resolvedSide, setResolvedSide] = useState(side);
  const [resolvedAlign, setResolvedAlign] = useState<Align>('center');
  const wrapperRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLSpanElement>(null);
  const id = useId();

  useLayoutEffect(() => {
    if (!visible) return;
    const wrapper = wrapperRef.current;
    const tooltip = tooltipRef.current;
    if (!wrapper || !tooltip) return;

    const wrapperRect = wrapper.getBoundingClientRect();
    const tooltipHeight = tooltip.offsetHeight;
    const tooltipWidth = tooltip.offsetWidth;

    if (side === 'top' && wrapperRect.top - tooltipHeight - EDGE_MARGIN < 0) {
      setResolvedSide('bottom');
    } else if (
      side === 'bottom' &&
      wrapperRect.bottom + tooltipHeight + EDGE_MARGIN > window.innerHeight
    ) {
      setResolvedSide('top');
    } else {
      setResolvedSide(side);
    }

    const centeredLeft = wrapperRect.left + wrapperRect.width / 2 - tooltipWidth / 2;
    const centeredRight = centeredLeft + tooltipWidth;
    if (centeredRight > window.innerWidth - EDGE_MARGIN) {
      setResolvedAlign('right');
    } else if (centeredLeft < EDGE_MARGIN) {
      setResolvedAlign('left');
    } else {
      setResolvedAlign('center');
    }
  }, [visible, side]);

  return (
    <span
      ref={wrapperRef}
      className={cn('relative inline-flex', className)}
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
      onFocus={() => setVisible(true)}
      onBlur={() => setVisible(false)}
    >
      <span aria-describedby={visible ? id : undefined} className="inline-flex">
        {children}
      </span>
      {visible ? (
        <span
          ref={tooltipRef}
          role="tooltip"
          id={id}
          className={cn(
            'pointer-events-none absolute z-50 w-max max-w-xs animate-fade-in text-balance',
            'rounded border border-border bg-surface-raised px-2 py-1 text-xs text-fg shadow-md',
            resolvedSide === 'top' ? 'bottom-full mb-1.5' : 'top-full mt-1.5',
            resolvedAlign === 'center' && 'left-1/2 -translate-x-1/2',
            resolvedAlign === 'left' && 'left-0',
            resolvedAlign === 'right' && 'right-0',
          )}
        >
          {content}
        </span>
      ) : null}
    </span>
  );
}
